import React from 'react';
import { Field, PrefixInput, T } from './ui';
import { cleanHandle, handleIssue } from '../../../utils/portalPageModel';
import { BRAND_DOMAIN } from '../../../utils/brand';

/**
 * HANDLE · YOUR PAGE LINK — the `heyportal.app/` prefix in grey, the handle in cyan, typed
 * characters stripped to `[a-z0-9_]` by `cleanHandle`, and the verdict from the one shared rule
 * (`handleIssue`). The refusal wording is the server's, so "reserved" and "taken" read the way it
 * decided them.
 *
 * Two places to put the verdict, same control underneath:
 *   `variant="error"`  — under the input, in the Field's reserved slot (Portal Editor).
 *   `variant="status"` — on the right of the label, `✓ AVAILABLE` / `3+ CHARACTERS` / `✗ TAKEN`
 *                        (Onboarding v2, which has no room for a second line).
 */
export default function HandleField({
  value, onChange, status, variant = 'error', height, maxLength = 50,
  label = 'HANDLE · YOUR PAGE LINK', hint = 'Letters, numbers and underscores.', showEmpty = true,
}) {
  const issue = handleIssue(value, status);
  // An empty box is "not finished", not "wrong": onboarding stays quiet until CONTINUE is pressed.
  const live = issue && (showEmpty || issue.kind !== 'empty') ? issue : null;
  const bad = !!live && live.kind !== 'checking';

  const input = (
    <PrefixInput
      prefix={`${BRAND_DOMAIN}/`}
      value={value}
      onChange={(v) => onChange(cleanHandle(v))}
      color="#22ECFB"
      fontSize={15}
      maxLength={maxLength}
      invalid={bad}
      placeholder="yourhandle"
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      data-testid="handle-input"
      style={height ? { height } : undefined}
    />
  );

  if (variant === 'error') {
    const available = !issue && (value || '').trim().length >= 3 && status?.available === true && status?.changed;
    return (
      <Field label={label} error={bad ? live.message : null} hint={live?.kind === 'checking' ? 'Checking…' : available ? 'Available.' : hint}>
        {input}
      </Field>
    );
  }

  const chip = statusChip(value, status);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, ...T.label }}>
        {label}
        {chip && <span style={{ color: chip.ok ? '#22ECFB' : '#FF66C4', whiteSpace: 'nowrap' }}>{chip.text}</span>}
      </span>
      {input}
      {hint && <span style={{ fontSize: 11, lineHeight: 1.5, color: '#6B6B6B' }}>{hint}</span>}
    </label>
  );
}

/** The short verdict beside the label. Nothing at all while the box is empty. */
export function statusChip(handle, status) {
  const issue = handleIssue(handle, status);
  if (!issue) return { ok: true, text: '✓ AVAILABLE' };
  if (issue.kind === 'empty') return null;
  if (issue.kind === 'short') return { ok: false, text: '3+ CHARACTERS' };
  if (issue.kind === 'checking') return { ok: true, text: 'CHECKING…' };
  return { ok: false, text: `✗ ${issue.message.toUpperCase().slice(0, 24)}` };
}
